import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { dataService, AddOnCategory } from '../../services/dataService';
import AddOnCategoryModal from './AddOnCategoryModal';

const AddOnCategories: React.FC = () => {
  const { tenantData } = useAuth();
  const [categories, setCategories] = useState<AddOnCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<AddOnCategory | null>(null);
  const [parentCategory, setParentCategory] = useState<AddOnCategory | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const loadCategories = useCallback(async () => {
    if (!tenantData?.id) return;
    
    try {
      console.log('Loading add-on categories for tenant:', tenantData.id);
      setLoading(true);
      const data = await dataService.getAddOnCategories(tenantData.id);
      console.log('Loaded categories:', data);
      setCategories(data);
    } catch (error) {
      console.error('Error loading add-on categories:', error);
      setError('Failed to load add-on categories');
    } finally {
      setLoading(false);
    }
  }, [tenantData]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const handleAddCategory = () => {
    setEditingCategory(null);
    setParentCategory(null);
    setShowModal(true);
  };

  const handleAddSubCategory = (parentCat: AddOnCategory) => {
    console.log('Adding sub-category for parent:', parentCat);
    setEditingCategory(null);
    setParentCategory(parentCat);
    setShowModal(true);
  };

  const handleEditCategory = (category: AddOnCategory) => {
    setEditingCategory(category);
    setShowModal(true);
  };

  const handleDeleteCategory = async (categoryId: string) => {
    if (!tenantData?.id) return;
    
    if (!window.confirm('Are you sure you want to delete this category? This will also delete all options in this category.')) {
      return;
    }

    try {
      await dataService.deleteAddOnCategory(tenantData.id, categoryId);
      await loadCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Failed to delete category: ' + (error as Error).message);
    }
  };

  const handleModalClose = () => {
    console.log('Closing modal, resetting state');
    setShowModal(false);
    setEditingCategory(null);
    setParentCategory(null);
  };

  const handleModalSuccess = () => {
    console.log('Modal success, refreshing categories');
    setShowModal(false);
    setEditingCategory(null);
    setParentCategory(null);
    loadCategories();
  };

  const toggleCategoryExpansion = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const moveCategory = async (categoryId: string, direction: 'up' | 'down') => {
    if (!tenantData?.id) return;
    
    const currentIndex = categories.findIndex(cat => cat.id === categoryId);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= categories.length) return;
    
    const newCategories = [...categories];
    const [movedCategory] = newCategories.splice(currentIndex, 1);
    newCategories.splice(newIndex, 0, movedCategory);
    
    // Update display order for all affected categories
    const updatePromises = newCategories.map((cat, index) => {
      if (cat.displayOrder !== index) {
        return dataService.updateAddOnCategory(tenantData.id, cat.id!, {
          displayOrder: index
        });
      }
      return Promise.resolve();
    });
    
    try {
      await Promise.all(updatePromises);
      await loadCategories();
    } catch (error) {
      console.error('Error reordering categories:', error);
      alert('Failed to reorder categories');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Add-On Categories</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage categories for add-ons like toppings, cheese, size options, etc.
          </p>
        </div>
        <button
          onClick={handleAddCategory}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Add Category</span>
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Categories List */}
      {categories.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 dark:text-gray-500 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No add-on categories yet</h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            Create your first add-on category to start offering customization options to customers.
          </p>
          <button
            onClick={handleAddCategory}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            Create First Category
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {categories.map((category, index) => (
            <div 
              key={category.id} 
              className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg ${
                category.isSubCategory ? 'ml-6 border-l-4 border-l-purple-200 dark:border-l-purple-700' : ''
              }`}
            >
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => toggleCategoryExpansion(category.id!)}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    >
                      {expandedCategories.has(category.id!) ? (
                        <ChevronDown className="w-5 h-5" />
                      ) : (
                        <ChevronUp className="w-5 h-5" />
                      )}
                    </button>
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white">{category.name}</h3>
                      {category.description && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">{category.description}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {/* Reorder buttons */}
                    <button
                      onClick={() => moveCategory(category.id!, 'up')}
                      disabled={index === 0}
                      className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title="Move up"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => moveCategory(category.id!, 'down')}
                      disabled={index === categories.length - 1}
                      className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title="Move down"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    
                    {/* Add Sub-Category button */}
                    <button
                      onClick={() => handleAddSubCategory(category)}
                      className="p-2 text-blue-400 hover:text-blue-600 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                      title="Add sub-category"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                    
                    {/* Edit button */}
                    <button
                      onClick={() => handleEditCategory(category)}
                      className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      title="Edit category"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    
                    {/* Delete button */}
                    <button
                      onClick={() => handleDeleteCategory(category.id!)}
                      className="p-2 text-red-400 hover:text-red-600 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      title="Delete category"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                {/* Category details */}
                <div className="mt-3 flex items-center space-x-6 text-sm text-gray-500 dark:text-gray-400">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    category.isRequired 
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200' 
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                  }`}>
                    {category.isRequired ? 'Required' : 'Optional'}
                  </span>
                  {category.isSubCategory && (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200">
                      Sub-Category
                    </span>
                  )}
                  <span>Min: {category.minSelections}</span>
                  <span>Max: {category.maxSelections === 0 ? 'Unlimited' : category.maxSelections}</span>
                  <span>Order: {category.displayOrder + 1}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <AddOnCategoryModal
          category={editingCategory}
          parentCategory={parentCategory}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
};

export default AddOnCategories;

